import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { FormattedTime } from './ui/formatted-time';
import { ACTION_SHELL_EXECUTE, type DeviceActionAudit } from '@/types';
import { colors } from '@/theme/tokens';

// The detail dialog used to be shell-only. After the audit log unified
// every operator action into one table, every row routes through this
// dialog — so the rendering has to be action-aware. Shell rows keep
// their dedicated stdout/stderr blocks (operators read these line-by-
// line); other rows show their params + result_data as JSON. The
// title also tracks the action so a screenshot.capture row doesn't
// open under "Shell Command Detail".

interface ActionAuditDetailDialogProps {
  audit: DeviceActionAudit | null;
  onClose: () => void;
}

// Reads the shell exit_code out of result_data. Only meaningful when
// audit.action === ACTION_SHELL_EXECUTE; returns undefined otherwise.
function shellExitCode(audit: DeviceActionAudit): number | undefined {
  const v = audit.result_data?.exit_code;
  return typeof v === 'number' ? v : undefined;
}

// Classifies an audit row into one of three visible states based on
// the unified result enum, with a shell-specific refinement: a failed
// shell row shows its exit code instead of the generic "Failed".
function classify(audit: DeviceActionAudit): {
  label: string;
  color: string;
  bgcolor: string;
} {
  if (audit.result === 'pending') {
    return { label: 'Pending', color: colors.status.pending.color, bgcolor: colors.status.pending.bg };
  }
  if (audit.result === 'failure') {
    const code = shellExitCode(audit);
    const label = audit.action === ACTION_SHELL_EXECUTE && typeof code === 'number' ? `Exit ${code}` : 'Failed';
    return { label, color: colors.status.blocked.color, bgcolor: colors.status.blocked.bg };
  }
  return { label: 'Success', color: colors.status.online.color, bgcolor: colors.status.online.bg };
}

// Human-readable title per action. The store/API names are dotted
// ("shell.execute", "device.reboot"); this maps them to readable
// dialog headers. Falls back to the canonical name so unknown / new
// action types still get a meaningful title.
function titleFor(action: string): string {
  switch (action) {
    case 'shell.execute':        return 'Shell Command Detail';
    case 'device.reboot':        return 'Reboot Detail';
    case 'device.factory_reset': return 'Factory Reset Detail';
    case 'device.delete':        return 'Device Deletion Detail';
    case 'device.get_info':      return 'Device Info Refresh Detail';
    case 'screenshot.capture':   return 'Screenshot Capture Detail';
    case 'screenshot.delete':    return 'Screenshot Delete Detail';
    case 'log.force_upload':     return 'Force Log Upload Detail';
    case 'log.download':         return 'Log Download Detail';
    case 'log.archive_download': return 'Log Archive Download Detail';
    case 'log.sync_gcs':         return 'Log Sync (GCS) Detail';
    case 'filesystem.list':      return 'Filesystem Listing Detail';
    case 'command.send':                  return 'Send Command Detail';
    case 'command.cancel':                return 'Cancel Command Detail';
    case 'monitor.live_logging.enable':   return 'Enable Live Logging Detail';
    case 'monitor.live_logging.disable':  return 'Disable Live Logging Detail';
    case 'monitor.memory.enable':         return 'Enable Memory Monitor Detail';
    case 'monitor.memory.disable':        return 'Disable Memory Monitor Detail';
    case 'monitor.activity.enable':       return 'Enable Activity Monitor Detail';
    case 'monitor.activity.disable':      return 'Disable Activity Monitor Detail';
    default:                     return `Action Detail — ${action}`;
  }
}

function CodeBlock({ label, content, errorLevel }: { label: string; content: string; errorLevel?: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can fail on insecure contexts or when the page
      // is not focused. Silently ignore, the text is still selectable.
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
          <IconButton size="small" onClick={handleCopy} disabled={!content}>
            <CopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          bgcolor: errorLevel ? '#FDF3F3' : '#F5F5F5',
          border: '1px solid',
          borderColor: errorLevel ? '#F8D7DA' : '#E0E0E0',
          borderRadius: 0.5,
          fontSize: 12,
          fontFamily: 'monospace',
          color: errorLevel ? '#C62828' : '#222',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 260,
          overflow: 'auto',
        }}
      >
        {content || <em style={{ color: '#999' }}>(empty)</em>}
      </Box>
    </Box>
  );
}

// Stringify-with-fallback for the params / result_data blocks. JSON
// is pretty-printed at 2-space indent so the block reads top-down on
// the common small-payload case.
function jsonOrEmpty(v: unknown): string {
  if (v === undefined || v === null) return '';
  if (typeof v === 'object' && Object.keys(v as object).length === 0) return '';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function ActionAuditDetailDialog({ audit, onClose }: ActionAuditDetailDialogProps) {
  const open = audit !== null;
  if (!audit) return null;
  const status = classify(audit);
  const isShell = audit.action === ACTION_SHELL_EXECUTE;
  const shellCmd = (audit.params?.command as string | undefined) ?? '';
  const stdout = (audit.result_data?.stdout as string | undefined) ?? '';
  const stderr = (audit.result_data?.stderr as string | undefined) ?? '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 5 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>{titleFor(audit.action)}</Typography>
        <Chip
          label={status.label}
          size="small"
          sx={{ height: 22, fontSize: 11, bgcolor: status.bgcolor, color: status.color }}
        />
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Executed at</Typography>
            <Box>
              <FormattedTime value={audit.executed_at} variant="auto" sx={{ fontSize: 13 }} />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Executed by</Typography>
            <Tooltip
              title={
                audit.executed_by_email ||
                (audit.executed_by ? 'No email recorded for this ID' : '')
              }
              placement="top"
              arrow
            >
              <Typography
                variant="body2"
                component="span"
                sx={{
                  fontSize: 13,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  display: 'inline-block',
                  cursor: audit.executed_by ? 'help' : 'default',
                }}
              >
                {audit.executed_by || '—'}
              </Typography>
            </Tooltip>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Action</Typography>
            <Typography variant="body2" sx={{ fontSize: 13, fontFamily: 'monospace' }}>
              {audit.action}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {isShell ? 'Exit code' : 'Completed at'}
            </Typography>
            {isShell ? (
              <Typography variant="body2" sx={{ fontSize: 13, fontFamily: 'monospace' }}>
                {shellExitCode(audit) ?? '—'}
              </Typography>
            ) : (
              <Box>
                {audit.completed_at ? (
                  <FormattedTime value={audit.completed_at} variant="auto" sx={{ fontSize: 13 }} />
                ) : (
                  <Typography variant="body2" sx={{ fontSize: 13, color: 'text.disabled' }}>—</Typography>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Audit id</Typography>
            <Typography variant="body2" sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary', wordBreak: 'break-all' }}>
              {audit.id}
            </Typography>
          </Box>
        </Box>

        {/* Error message, when present. Always rendered for failure
            rows so an operator doesn't have to dig into result_data
            to figure out why it failed. */}
        {audit.error && (
          <CodeBlock label="Error" content={audit.error} errorLevel />
        )}

        {/* Shell rows keep their dedicated panes — operators read
            stdout/stderr line-by-line and a generic JSON dump would
            bury the signal. Command + stdout + stderr live in
            params/result_data, pulled out for display. */}
        {isShell && (
          <>
            <CodeBlock label="Command" content={shellCmd} />
            <CodeBlock label="stdout" content={stdout} />
            <CodeBlock label="stderr" content={stderr} errorLevel />
          </>
        )}

        {/* Generic blocks for every other action. Reboot's {delayMs},
            screenshot's {mode}, filesystem.list's {path,entry_count},
            log download's {file_id,file_name,file_size}, etc. — same
            shape the backend wrote into the audit row. */}
        {!isShell && (
          <>
            {jsonOrEmpty(audit.params) && (
              <CodeBlock label="Params" content={jsonOrEmpty(audit.params)} />
            )}
            {jsonOrEmpty(audit.result_data) && (
              <CodeBlock label="Result" content={jsonOrEmpty(audit.result_data)} />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
